import './SocialPlanCard.css';

import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@mui/material';
import { calculateTimeProgress } from '../../../../../b2b-plans/components/helper';
import usePlanStatusTrendProgressData from '../../../../../b2b-plans/hooks/usePlanStatusTrendProgressData';
import usePlansFrameworks from '../../../../../b2b-plans/hooks/usePlansFrameworks';
import { fullImagePath } from '../../../../../b2b-common/helpers';
import StatusTrendFactory from '../../../../../b2b-plans/components/status-trend/StatusTrendFactory';
import { FwRelated } from '../../../../../b2b-plans/components/FwsRelated';
import DatesRangeFactory from '../../../../../b2b-plans/components/dates/DatesRangeFactory';
import ProgressFactory from '../../../../../b2b-plans/components/progress/ProgressFactory';
import { isEqual } from 'lodash';


function updatePlan(plan, showStatus = true, showTrend = true, showDatesRange = true, showCalendarProgress = true, showTargetProgress = true) {
    let updatedPlan = { ...plan };

    if (!updatedPlan.snapshot) {
        updatedPlan.snapshot = {};
    }

    if (!showStatus) {
        updatedPlan.snapshot.status = null;
    }
    if (!showTrend) {
        updatedPlan.snapshot.trend = null;
    }
    if (!showDatesRange) {
        updatedPlan.planStartDate = null;
        updatedPlan.planEndDate = null;
    }
    if (!showTargetProgress) {
        updatedPlan.snapshot.progress = null;
    }
    if (!showCalendarProgress) {
        updatedPlan.snapshot.calendarProgress = null;
    }
    // other fields not required to be in the plan payload
    delete updatedPlan.planId;
    delete updatedPlan.otherImages;
    delete updatedPlan.areaDTOs;
    delete updatedPlan.planStatus;
    delete updatedPlan.readOnly;
    delete updatedPlan.userUUID;

    return updatedPlan;
}

const updatePlanWithStatusTrendProgress = (planData, setPlanData, planStatusTrendProgressData) => {
    if (planStatusTrendProgressData) {
        const copyOfPlanStatusTrendProgressData = { ...planStatusTrendProgressData };
        const toUpdatePlanData = { ...planData };
        toUpdatePlanData.plan.snapshot = copyOfPlanStatusTrendProgressData;
        toUpdatePlanData.plan.snapshot.calendarProgress = calculateTimeProgress(planData.plan.planStartDate, planData.plan.planEndDate);
        setPlanData(toUpdatePlanData);
        return toUpdatePlanData;
    }
    return planData;
}

const updatePlanWithSDGs = (planData, setPlanData, sdgs2030) => {
    if (sdgs2030) {
        const copyOfPlanData = { ...planData };
        copyOfPlanData.plan.sdgs2030 = sdgs2030.level1sDTOs.map(l1 => l1.levelN_ItemId);
        setPlanData(copyOfPlanData);
        return copyOfPlanData;
    }
    return planData;
}

const SocialPlanCard = ({ planData, selectedPlans = [],
    showStatus = true, showTrend = true,
    showDatesRange = true,
    showCalendarProgress = true, showTargetProgress = true,
    onSelect, onSelectedPlanChange }) => {

    const [cardHovered, setCardHovered] = useState(false);
    const [currentPlanData, setCurrentPlanData] = useState(null);
    const [sdgs2030, setSdgs2030] = useState(null);

    const [currentSelectedPlans, setCurrentSelectedPlans] = useState(null);
    const [currentShowStatus, setCurrentShowStatus] = useState(showStatus);
    const [currentShowTrend, setCurrentShowTrend] = useState(showTrend);
    const [currentShowDatesRange, setCurrentShowDatesRange] = useState(showDatesRange);
    const [currentShowCalendarProgress, setCurrentShowCalendarProgress] = useState(showCalendarProgress);
    const [currentShowTargetProgress, setCurrentShowTargetProgress] = useState(showTargetProgress);

    const { planStatusTrendProgressData } = usePlanStatusTrendProgressData(currentPlanData?.plan.planUUID);
    const { genericFwsRelated } = usePlansFrameworks(currentPlanData?.plan.planUUID, [{ frameworkSuite: 'generic', fwIds: [2] }]);
    
    useEffect(() => {
        if (!isEqual(selectedPlans, currentSelectedPlans)) {
            // console.log('currentPlanData', currentPlanData);
            // console.log('currentSelectedPlans', currentSelectedPlans);
            // console.log('selectedPlans', selectedPlans);
            setCurrentSelectedPlans(selectedPlans);
        }
    }, [selectedPlans]);

    useEffect(() => {
        if  (((planData && !currentPlanData) ||
            (planData && currentPlanData)) && (!isEqual(planData, currentPlanData))) {
            setSdgs2030(null);
            setCurrentPlanData(planData);
            updatePlanWithStatusTrendProgress(planData, setCurrentPlanData, planStatusTrendProgressData);
        } 
    }, [planData]);

    useEffect(() => {
        if (currentPlanData && planStatusTrendProgressData) {
            updatePlanWithStatusTrendProgress(currentPlanData, setCurrentPlanData, planStatusTrendProgressData);
        }
    }, [planStatusTrendProgressData]);

    useEffect(() => {
        if (currentPlanData && genericFwsRelated && genericFwsRelated.length == 1 && genericFwsRelated[0].level1sDTOs.length > 0) {
            setSdgs2030(genericFwsRelated[0]);
            const wholePlan = updatePlanWithSDGs(currentPlanData, setCurrentPlanData, genericFwsRelated[0]);
            const updatedPlan = updatePlan(wholePlan.plan, showStatus, showTrend, showDatesRange, showCalendarProgress, showTargetProgress);
            onSelectedPlanChange && onSelectedPlanChange(updatedPlan);
        } else {
            setSdgs2030(null);
        }
    }, [genericFwsRelated]);

    useEffect(() => {
        if (currentShowStatus !== showStatus) setCurrentShowStatus(showStatus);
        if (currentShowTrend !== showTrend) setCurrentShowTrend(showTrend);
        if (currentShowDatesRange !== showDatesRange) setCurrentShowDatesRange(showDatesRange);
        if (currentShowCalendarProgress !== showCalendarProgress) setCurrentShowCalendarProgress(showCalendarProgress);
        if (currentShowTargetProgress !== showTargetProgress) setCurrentShowTargetProgress(showTargetProgress);
        if (currentPlanData && currentSelectedPlans.includes(currentPlanData.plan.planUUID)) {
            const wholePlan = updatePlanWithStatusTrendProgress(currentPlanData, setCurrentPlanData, planStatusTrendProgressData);
            const updatedPlan = updatePlan(wholePlan.plan, showStatus, showTrend, showDatesRange, showCalendarProgress, showTargetProgress);
            onSelectedPlanChange && onSelectedPlanChange(updatedPlan);
        }
    }, [showStatus, showTrend, showDatesRange, showCalendarProgress, showTargetProgress]);

    useEffect(() => {
        if (planStatusTrendProgressData && currentPlanData) {
            updatePlanWithStatusTrendProgress(currentPlanData, setCurrentPlanData, planStatusTrendProgressData);
        }
    }, [planStatusTrendProgressData]);

    if (!currentPlanData) return null;

    const isSelected = currentSelectedPlans.includes(currentPlanData.plan.planUUID);

    const handleClickOnCard = () => {
        const wholePlan = updatePlanWithStatusTrendProgress(currentPlanData, setCurrentPlanData, planStatusTrendProgressData);
        const updatedPlan = updatePlan(wholePlan.plan, showStatus, showTrend, showDatesRange, showCalendarProgress, showTargetProgress);
        onSelect && onSelect(updatePlan(updatedPlan));
    }

    return (
        <Card variant='outlined'
            onMouseEnter={() => setCardHovered(true)}
            onMouseLeave={() => setCardHovered(false)}
            className={`social-plan-card ${isSelected ? 'plan-selected' : ''} ${cardHovered ? 'plan-card-hovered' : ''} `}
            onClick={handleClickOnCard}
        >
            <div className='social-plan-card-header'>
                <img src={fullImagePath(currentPlanData.plan.imagePath)} alt={currentPlanData.plan.planName} className='social-plan-card-image' />
                <StatusTrendFactory approach='COMBINED' status={currentPlanData.plan.snapshot?.status} trend={currentPlanData.plan.snapshot?.trend}
                    showStatus={currentShowStatus} showTrend={currentShowTrend}
                    className='social-plan-card-status-and-trend' />
                <FwRelated framework={sdgs2030} className='social-plan-card-sdgs'
                    showLogo={false} 
                />
            </div>
            <CardContent className='social-plan-card-content' >
                <div className='social-plan-card-name'>
                    {currentPlanData.plan.planName}
                </div>
                {currentShowDatesRange && (
                    <DatesRangeFactory datesRangeType='SHORT' startDate={currentPlanData.plan.planStartDate} endDate={currentPlanData.plan.planEndDate} className={'social-plan-card-dates-range'} />
                )}
                <ProgressFactory progressApproach='SIMPLE' targetProgress={planStatusTrendProgressData ? planStatusTrendProgressData.progress : 0}
                    timeProgress={calculateTimeProgress(currentPlanData.plan.planStartDate, currentPlanData.plan.planEndDate)}
                    showCalendarProgress={currentShowCalendarProgress} showTargetProgress={currentShowTargetProgress}
                />
                <div className='social-plan-card-description' dangerouslySetInnerHTML={{ __html: currentPlanData.plan.planDescription }} />
            </CardContent>
        </Card>
    );
}

export default SocialPlanCard;